import { useCallback, useEffect, useState } from "react";
import { Button, Group, Select, Stack, Switch, Table, Text, TextInput } from "@mantine/core";

import { TablePagination } from "@components/Table";

import { api } from "@utils/api";

import { PrimarySpecialization, primarySpecializationOptions } from "@hooks/queries/profile/usePrimarySpecializations";


const CATALOG_PAGE_SIZE = 25;
const PROFILES_PAGE_SIZE = 50;

interface UnmappedProfile { _id: string; specialization?: string; user_info?: { full_name?: string } }

export function PrimarySpecializationsTab() {
  const [entries, setEntries] = useState<PrimarySpecialization[]>([]);
  const [profiles, setProfiles] = useState<UnmappedProfile[]>([]);
  const [en, setEn] = useState("");
  const [fr, setFr] = useState("");
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<Record<string, string | null>>({});
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [catalogPage, setCatalogPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const catalogPageCount = Math.max(1, Math.ceil(entries.length / CATALOG_PAGE_SIZE));
  const currentCatalogPage = Math.min(catalogPage, catalogPageCount);
  const visibleEntries = entries.slice((currentCatalogPage - 1) * CATALOG_PAGE_SIZE, currentCatalogPage * CATALOG_PAGE_SIZE);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [catalog, unmapped] = await Promise.all([
        api.get("primary-specializations/admin"),
        api.get("profile/", { params: { unmapped: true, visibility: "all", type: "doctor", page, page_size: PROFILES_PAGE_SIZE } }),
      ]);
      setEntries(catalog.data);
      setProfiles(unmapped.data.data || []);
      setTotal(unmapped.data.pagination?.total || 0);
      const lastPage = Math.max(1, Math.ceil((unmapped.data.pagination?.total || 0) / PROFILES_PAGE_SIZE));
      if (page > lastPage) setPage(lastPage);
      setError("");
    } catch (reason: any) {
      setError(reason?.response?.data?.detail || "Could not load specializations");
    } finally {
      setIsLoading(false);
    }
  }, [page]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setCatalogPage(currentCatalogPage); }, [currentCatalogPage]);

  const save = async (id: string, changes: Record<string, unknown>) => {
    try {
      await api.patch(`primary-specializations/${id}`, changes);
      await load();
    } catch (reason: any) {
      setError(reason?.response?.data?.detail || "Could not save specialization");
    }
  };

  const assign = async (profileId: string) => {
    const id = selected[profileId];
    if (!id) return;
    try {
      const data = new FormData();
      data.append("changes", JSON.stringify({ primary_specialization_id: id }));
      await api.patch(`profile/${profileId}/admin`, data, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (reason: any) {
      setError(reason?.response?.data?.detail || "Could not assign specialization");
    }
  };

  return <Stack gap="lg" mb="8rem">
    <Text fw={600}>Primary specializations</Text>
    {error && <Text c="red">{error}</Text>}
    <Group align="end">
      <TextInput label="English name" value={en} onChange={event => setEn(event.currentTarget.value)} />
      <TextInput label="French name" value={fr} onChange={event => setFr(event.currentTarget.value)} />
      <Button disabled={!en.trim() || !fr.trim()} onClick={async () => {
        try {
          await api.post("primary-specializations", { labels: { en: en.trim(), fr: fr.trim() } });
          setEn(""); setFr(""); await load();
        } catch (reason: any) { setError(reason?.response?.data?.detail || "Could not create specialization"); }
      }}>Create</Button>
    </Group>
    <Table striped>
      <Table.Thead><Table.Tr><Table.Th>English</Table.Th><Table.Th>French</Table.Th><Table.Th>Profiles</Table.Th><Table.Th>Active</Table.Th><Table.Th>Actions</Table.Th></Table.Tr></Table.Thead>
      <Table.Tbody>{visibleEntries.map(entry => <Table.Tr key={entry._id}>
        <Table.Td><TextInput defaultValue={entry.labels.en} id={`en-${entry._id}`} /></Table.Td>
        <Table.Td><TextInput defaultValue={entry.labels.fr} id={`fr-${entry._id}`} /></Table.Td>
        <Table.Td>{entry.profile_count || 0}</Table.Td>
        <Table.Td><Switch checked={entry.active} onChange={event => void save(entry._id, { active: event.currentTarget.checked })} /></Table.Td>
        <Table.Td><Group>
          <Button size="xs" onClick={() => void save(entry._id, { labels: {
            en: (document.getElementById(`en-${entry._id}`) as HTMLInputElement).value.trim(),
            fr: (document.getElementById(`fr-${entry._id}`) as HTMLInputElement).value.trim(),
          } })}>Save</Button>
          <Button size="xs" color="red" disabled={!!entry.profile_count || !!entry.draft_count} onClick={async () => {
            if (!window.confirm("Delete this unused specialization?")) return;
            try { await api.delete(`primary-specializations/${entry._id}`); await load(); }
            catch (reason: any) { setError(reason?.response?.data?.detail || "Could not delete specialization"); }
          }}>Delete</Button>
        </Group></Table.Td>
      </Table.Tr>)}</Table.Tbody>
    </Table>
    <TablePagination
      paginationData={{ page: currentCatalogPage, pageSize: CATALOG_PAGE_SIZE, pageCount: catalogPageCount, total: entries.length }}
      paginatedRows={visibleEntries.length}
      onPageChange={setCatalogPage}
      isLoading={isLoading}
    />
    <Text fw={600}>Profiles needing a specialization ({total})</Text>
    <Table striped><Table.Thead><Table.Tr><Table.Th>Doctor</Table.Th><Table.Th>Original text</Table.Th><Table.Th>Assign</Table.Th></Table.Tr></Table.Thead>
      <Table.Tbody>{profiles.map(profile => <Table.Tr key={profile._id}>
        <Table.Td>{profile.user_info?.full_name || profile._id}</Table.Td>
        <Table.Td>{profile.specialization || "—"}</Table.Td>
        <Table.Td><Group><Select searchable data={primarySpecializationOptions(entries)} value={selected[profile._id] || null}
          onChange={value => setSelected(previous => ({ ...previous, [profile._id]: value }))} />
          <Button size="xs" disabled={!selected[profile._id]} onClick={() => void assign(profile._id)}>Assign</Button></Group></Table.Td>
      </Table.Tr>)}</Table.Tbody></Table>
    <TablePagination
      paginationData={{ page, pageSize: PROFILES_PAGE_SIZE, pageCount: Math.max(1, Math.ceil(total / PROFILES_PAGE_SIZE)), total }}
      paginatedRows={profiles.length}
      onPageChange={setPage}
      isLoading={isLoading}
    />
  </Stack>;
}
